import { z } from 'zod';
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const instant = z.string().datetime({ offset: true });
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value, 'invalid_calendar_date');
export const researchStatusSchema = z.enum(['complete', 'partial', 'blocked']);
export const researchRunSchema = z.object({
    schemaVersion: z.literal('research-run-v1'),
    runId: z.string().regex(/^[a-zA-Z0-9_-]{8,100}$/),
    title: z.string().min(1).max(100),
    generatedAt: instant,
    asOf: day,
    status: researchStatusSchema,
    researchOnly: z.literal(true),
    provenance: z.object({ inputHash: digest, codeHash: digest, parameterHash: digest, contract: z.string().max(100) }),
    sections: z.array(z.object({
        key: z.enum(['labels', 'factors', 'portfolio']),
        title: z.string().max(100),
        status: researchStatusSchema,
        description: z.string().max(1200),
        metrics: z.array(z.object({ label: z.string().max(80), value: z.union([z.number().finite(), z.string().max(160)]).nullable(), unit: z.string().max(20).default('') })).max(20),
        warnings: z.array(z.string().max(600)).max(30)
    })).min(1).max(3),
    factors: z.array(z.object({
        name: z.string().max(100), meanIc: z.number().min(-1).max(1).nullable(),
        ciLower: z.number().nullable(), ciUpper: z.number().nullable(),
        dates: z.number().int().nonnegative(), coveragePct: z.number().min(0).max(100).nullable(),
        status: z.string().max(100)
    })).max(40).default([]),
    series: z.array(z.object({
        name: z.string().max(100), unit: z.string().max(20),
        points: z.array(z.object({ date: day, value: z.number().finite().nullable() })).max(2500)
    })).max(4).default([]),
    limitations: z.array(z.string().max(600)).max(40)
}).strict().superRefine((run, ctx) => {
    if (new Set(run.sections.map(section => section.key)).size !== run.sections.length)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate_research_section' });
    if (run.status === 'complete' && run.sections.some(section => section.status !== 'complete'))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'incomplete_section_cannot_be_complete' });
    if (run.series.length && !run.sections.some(section => section.key === 'portfolio' && section.status === 'complete'))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'series_require_complete_portfolio' });
    for (const series of run.series) {
        if (series.points.some((point, index) => index > 0 && point.date <= series.points[index - 1].date))
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'series_dates_must_increase' });
        if (series.points.some(point => point.date > run.asOf))
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'series_cannot_exceed_cutoff' });
    }
});
export const fundamentalObservationSchema = z.object({
    code: z.string().regex(/^\d{6}$/), reportPeriod: z.string().regex(/^\d{8}$/),
    observedAt: instant, availableAt: instant, contentHash: digest,
    knowledgeQuality: z.literal('observed_snapshot_not_as_filed'),
    sourceName: z.string().max(100),
    metrics: z.array(z.object({ label: z.string().max(50), value: z.number().finite().nullable(), unit: z.string().max(20) })).max(20)
});
export const technicalObservationSchema = z.object({
    asOfDate: day, observedAt: instant, adjustment: z.literal('qfq'),
    barCount: z.number().int().min(1).max(160),
    sourceName: z.string().min(1), sourceUrl: z.string().url(), contentHash: digest,
    metrics: z.array(z.object({ key: z.string().max(60), label: z.string().max(80), value: z.number().finite().nullable(), unit: z.string().max(20) })).max(20),
    warnings: z.array(z.string().max(600)).max(20)
});
export const stockResearchDocumentSchema = z.object({
    schemaVersion: z.literal('stock-research-v1'), documentId: digest,
    code: z.string().regex(/^\d{6}$/), name: z.string().nullable(), capturedAt: instant,
    quote: z.object({
        quoteAt: instant, price: z.number().positive().nullable(), previousClose: z.number().positive().nullable(),
        open: z.number().nonnegative().nullable(), high: z.number().nonnegative().nullable(), low: z.number().nonnegative().nullable(),
        changePct: z.number().nullable(), volumeShares: z.number().nonnegative().nullable(), amountCny: z.number().nonnegative().nullable(),
        sourceName: z.string(), sourceUrl: z.string().url(), contentHash: digest
    }).nullable(),
    fundamental: fundamentalObservationSchema.nullable(),
    technical: technicalObservationSchema.nullable().optional(),
    announcements: z.array(z.object({
        title: z.string().max(300), publishedDate: day, availableAt: instant,
        sourceName: z.string(), sourceUrl: z.string().url(), contentHash: digest,
        evidenceType: z.literal('notice_metadata')
    })).max(10),
    coverage: z.object({ quote: z.enum(['available', 'unavailable']), fundamental: z.enum(['available', 'unavailable']), announcements: z.enum(['available', 'empty', 'unavailable']), technical: z.enum(['available', 'unavailable']).optional() }),
    limitations: z.array(z.string().max(600)),
    errors: z.array(z.object({ source: z.string(), code: z.string() })),
    analysisEligible: z.boolean()
});
